import React, { useState } from 'react';
import { Download, Eye, FileText } from 'lucide-react';
import { computeCalculsPDL } from '../../utils/calculsTurpeRapport';
import { MessageFournisseurButton } from './MessageFournisseur';

/**
 * Échantillonne un graphData pour limiter la taille du payload (rapports multi ~200 PDL).
 * Conserve la forme de la courbe (répartition uniforme + dernier point).
 */
const capGraphData = (arr, max = 500) => {
  if (!Array.isArray(arr) || arr.length <= max) return arr;
  const step = arr.length / max;
  const out = [];
  for (let i = 0; i < max; i++) out.push(arr[Math.floor(i * step)]);
  const dernier = arr[arr.length - 1];
  if (out[out.length - 1] !== dernier) out.push(dernier);
  return out;
};

/**
 * Génère un PDF TURPE via une tâche de fond (polling) pour éviter le timeout 504 de la
 * passerelle sur les gros dossiers (~200 PDL, plusieurs dizaines de secondes de rendu).
 * Retourne un Blob PDF téléchargeable.
 */
const generatePdfViaJob = async (payload, onTick) => {
  const API = process.env.REACT_APP_BACKEND_URL;
  const startRes = await fetch(`${API}/api/turpe/rapports/pdf/async`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!startRes.ok) throw new Error('Impossible de démarrer la génération du PDF');
  const { job_id } = await startRes.json();

  const maxMs = 5 * 60 * 1000; // 5 min max
  const interval = 2500;
  let waited = 0;
  while (waited < maxMs) {
    await new Promise((r) => setTimeout(r, interval));
    waited += interval;
    if (onTick) onTick(Math.round(waited / 1000));
    const st = await fetch(`${API}/api/turpe/rapports/pdf/async/${job_id}/status`);
    if (!st.ok) throw new Error('Suivi de la génération indisponible');
    const { status, error } = await st.json();
    if (status === 'done') break;
    if (status === 'error') throw new Error(error || 'Erreur lors de la génération du PDF');
  }
  const res = await fetch(`${API}/api/turpe/rapports/pdf/async/${job_id}/result`);
  if (!res.ok) throw new Error('PDF non disponible (délai dépassé)');
  return res.blob();
};

/**
 * Étape 7 TURPE — extrait de CreateAnalyseTURPE.jsx pour réduire la taille
 * du composant parent. Reçoit toutes les dépendances via un objet `ctx` unique.
 */
export const Step7Generation = ({ ctx }) => {
  const {
    agences,
    agregerParHeure,
    analyseType,
    calculerConsommationsParPlage,
    calculsParPDL,
    calculsRapport,
    cerOverrideRef,
    cmdpsOverrideRef,
    commentairesParPDL,
    commerciaux,
    consommationsParPlageRef,
    currentPDL,
    determinerPlageHoraire,
    editMode,
    editRapportId,
    formData,
    plagesHorairesCustomRef,
    preconisationsPuissanceParPlageRef,
    preconisationsPuissanceRef,
    puissancesFacturesParPDL,
    puissancesMaxParPDL,
    sauvegarderBrouillon,
    setStep,
  } = ctx;

  // États locaux pour l'UI de génération PDF asynchrone (progress bar).
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [pdfElapsed, setPdfElapsed] = useState(0);

  // Dépendances pour le recalcul par PDL au moment de la génération (lues à l'instant du clic).
  const buildDepsCalc = () => ({
    formData,
    preconisationsPuissance: preconisationsPuissanceRef.current,
    preconisationsPuissanceParPlage: preconisationsPuissanceParPlageRef.current,
    consommationsParPlage: consommationsParPlageRef.current,
    cerOverride: cerOverrideRef.current,
    cmdpsOverride: cmdpsOverrideRef.current,
    determinerPlageHoraire,
    calculerConsommationsParPlage,
  });

  // Normalise les PDL pour le "Message fournisseur" (recalcul complet, indépendant de la pagination).
  const getMessagePdls = () => {
    const depsCalc = buildDepsCalc();
    return (formData?.pdls || []).map((pc) => {
      const key = pc.pdl;
      const res = puissancesMaxParPDL[key];
      const c = computeCalculsPDL(key, res, depsCalc) || calculsParPDL[key] || {};
      const pa = pc.puissances_actuelles || {};
      const souscrite = Math.max(0, ...Object.values(pa).map((v) => Number(v) || 0));
      return {
        pdl: { numero: key, puissanceSouscrite: souscrite },
        tensionActuelle: c.tensionActuelle || pc.tension || 'HTA',
        formuleActuelle: c.formuleActuelle || pc.formule_actuelle || '',
        formuleProposee: c.formuleProposee || pc.formule_actuelle || '',
        calculs: c,
      };
    });
  };

  return (
        <div className="bg-white border border-[#BFC6CC] rounded-lg p-8">
          <h2 className="text-2xl font-heading font-semibold text-[#191D1E] mb-2">
            Génération du Rapport TURPE {analyseType === 'multi' ? 'Multi-Sites' : ''}
          </h2>
          <p className="text-[#687175] mb-8">
            {analyseType === 'multi' 
              ? `Rapport consolidé pour ${formData.pdls.length} points de livraison`
              : 'Rapport d\'optimisation de la puissance souscrite'
            }
          </p>

          <div className="p-12 bg-[#F5F6F9] rounded-lg text-center space-y-6">
            <div className="w-20 h-20 bg-[#397DFF]/10 rounded-full flex items-center justify-center mx-auto">
              <FileText className="w-10 h-10 text-[#397DFF]" />
            </div>
            <div>
              <h3 className="text-xl font-semibold text-[#191D1E] mb-2">Rapport prêt à être généré</h3>
              <p className="text-[#687175]">
                {analyseType === 'multi'
                  ? 'Le rapport contiendra une synthèse globale et le détail de chaque site.'
                  : 'Le rapport contiendra l\'analyse complète avec les préconisations d\'optimisation TURPE.'
                }
              </p>
            </div>
            
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              {/* Bouton Rapport Interactif */}
              <button
                onClick={async () => {
                  if (analyseType === 'multi') {
                    // === RAPPORT MULTI-PDL ===
                    const depsCalc = buildDepsCalc();
                    const pdlsData = formData.pdls.map((pdlConfig, idx) => {
                      const pdlKey = pdlConfig.pdl;
                      const resultatPDL = puissancesMaxParPDL[pdlKey];
                      // Recalcul complet du PDL au moment de la génération (indépendant du rendu
                      // paginé de l'étape 6) → évite les PDL à 0 sur les gros dossiers (~200 PDL).
                      const calculsPDL = computeCalculsPDL(pdlKey, resultatPDL, depsCalc) || calculsParPDL[pdlKey] || {};
                      const tousLesPoints = resultatPDL?.tousLesPoints || [];
                      // Agréger par heure - pas de limite pour avoir toutes les données
                      const graphDataRaw = capGraphData(agregerParHeure(tousLesPoints)
                        .map(p => ({ date: p.horodate, kW: p.kW, kVA: p.kVA })));
                      
                      // Récupérer les calculs pour ce PDL depuis calculsParPDL
                      const consoParPlage = calculsPDL.consommations || consommationsParPlageRef.current[pdlKey] || {};
                      const puissancesActuellesParPlage = calculsPDL.puissancesActuelles || pdlConfig.puissances_actuelles || {};
                      const puissMax = calculsPDL.puissanceSouscrite || Math.max(
                        puissancesActuellesParPlage?.Pointe || 0,
                        puissancesActuellesParPlage?.HPH || 0,
                        puissancesActuellesParPlage?.HCH || 0,
                        puissancesActuellesParPlage?.HPB || 0,
                        puissancesActuellesParPlage?.HCB || 0,
                        puissancesActuellesParPlage?.max || 0,
                        resultatPDL?.puissanceMax || 0
                      );
                      const puissPreconisee = calculsPDL.puissancePreconisee || preconisationsPuissanceRef.current[pdlKey] || puissMax;
                      
                      return {
                        pdl: {
                          numero: pdlKey,
                          puissanceSouscrite: puissMax
                        },
                        tensionActuelle: pdlConfig.tension || 'HTA',
                        formuleActuelle: pdlConfig.formule_actuelle || '',
                        formuleProposee: calculsPDL.formuleProposee || preconisationsPuissanceRef.current[`${pdlKey}_formule`] || pdlConfig.formule_actuelle || '',
                        commentaire: commentairesParPDL[pdlKey] || '', // Commentaire de l'energy manager
                        calculs: {
                          totalActuel: calculsPDL.totalActuel || 0,
                          totalPropose: calculsPDL.totalPropose || 0,
                          composantes: calculsPDL.composantes || {},
                          consommations: consoParPlage,
                          puissancesActuelles: puissancesActuellesParPlage,
                          puissancesProposees: calculsPDL.puissancesProposees || { max: puissPreconisee },
                          puissancePreconisee: puissPreconisee,
                          cmdpsOverride: calculsPDL.cmdpsOverride || null
                        },
                        graphData: graphDataRaw,
                        puissancesFactures: (() => {
                          const pf = puissancesFacturesParPDL[pdlKey] || {};
                          const entries = Object.entries(pf).filter(([,v]) => v > 0);
                          return entries.length > 0 ? entries.map(([mois, puissance]) => ({ mois, puissance })) : undefined;
                        })()
                      };
                    });
                    
                    // Calculer les totaux
                    const totalActuel = pdlsData.reduce((sum, p) => sum + (p.calculs?.totalActuel || 0), 0);
                    const totalPropose = pdlsData.reduce((sum, p) => sum + (p.calculs?.totalPropose || 0), 0);
                    
                    const rapportMultiData = {
                      client: {
                        nom: formData.nom_client || '',
                        siren: formData.siren || ''
                      },
                      siren: formData.siren || '',
                      nom_entreprise: formData.nom_client || '',
                      pdls: pdlsData,
                      totaux: {
                        totalActuel,
                        totalPropose,
                        economie: totalActuel - totalPropose
                      },
                      economies: totalActuel - totalPropose,
                      commercial_id: formData.commercial_id,
                      agence_id: formData.agence_id
                    };
                    
                    // Envoyer au backend et récupérer un ID de preview
                    try {
                      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/turpe/rapports/multi/preview`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(rapportMultiData)
                      });
                      if (response.ok) {
                        const { preview_id } = await response.json();
                        window.open(`/rapports/turpe/multi/preview?preview=${preview_id}`, '_blank');
                        
                        // Sauvegarder aussi le rapport en base de données
                        const token = localStorage.getItem('token');
                        const saveUrl = editMode 
                          ? `${process.env.REACT_APP_BACKEND_URL}/api/turpe/rapports/${editRapportId}`
                          : `${process.env.REACT_APP_BACKEND_URL}/api/turpe/rapports`;
                        const saveResponse = await fetch(saveUrl, {
                          method: editMode ? 'PUT' : 'POST',
                          headers: { 
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                          },
                          body: JSON.stringify(rapportMultiData)
                        });
                        if (saveResponse.ok) {
                          console.log('Rapport TURPE multi-sites sauvegardé en base');
                        } else {
                          console.warn('Sauvegarde en base échouée:', await saveResponse.text());
                        }
                      } else {
                        alert('Erreur lors de la création de la preview');
                      }
                    } catch (error) {
                      console.error('Erreur:', error);
                      alert('Erreur lors de la création de la preview');
                    }
                  } else {
                    // === RAPPORT MONO-PDL ===
                    const pdlData = formData.pdls[0] || currentPDL;
                    const pdlKey = calculsRapport?.pdl || pdlData?.pdl || Object.keys(puissancesMaxParPDL)[0];
                    
                    const resultatPDL = puissancesMaxParPDL[pdlKey];
                    const tousLesPoints = resultatPDL?.tousLesPoints || [];
                    // Agréger par heure et renommer 'horodate' en 'date' pour le graphique
                    // Augmenter la limite pour avoir toutes les données (8760 heures = 1 an)
                    const graphDataRaw = agregerParHeure(tousLesPoints)
                      .map(p => ({ date: p.horodate, kW: p.kW, kVA: p.kVA }));
                    
                    // Récupérer la puissance souscrite max
                    const puissanceSouscrite = calculsRapport?.puissanceSouscrite 
                      || pdlData?.puissances_actuelles?.max 
                      || pdlData?.puissances_actuelles?.Pointe 
                      || resultatPDL?.puissanceMax 
                      || 0;
                    
                    // Puissance préconisée
                    const puissancePreconisee = calculsRapport?.puissancePreconisee 
                      || calculsRapport?.puissanceProposee 
                      || preconisationsPuissanceRef.current[pdlKey] 
                      || puissanceSouscrite;
                    
                    const rapportData = {
                      client: {
                        nom: formData.nom_client || '',
                        siren: '',
                        site: ''
                      },
                      pdl: {
                        numero: calculsRapport?.pdl || pdlData?.pdl || '',
                        puissanceSouscrite: puissanceSouscrite
                      },
                      commentaire: commentairesParPDL[pdlKey] || '', // Commentaire de l'energy manager
                      calculs: {
                        totalActuel: calculsRapport?.totalActuel || 0,
                        totalPropose: calculsRapport?.totalPropose || 0,
                        composantes: calculsRapport?.composantes || {},
                        consommations: calculsRapport?.consommations || {},
                        nbJours: calculsRapport?.nbJoursData || 0,
                        consoAnnuelleKWh: calculsRapport?.consoAnnuelleKWh || 0,
                        tanPhiMoyen: calculsRapport?.tanPhiMoyen || 0,
                        tanPhiHPH: calculsRapport?.tanPhiHPH || 0,
                        energieReactiveFacturable: calculsRapport?.energieReactiveFacturable || 0,
                        depassements: calculsRapport?.depassements || {},
                        puissancesActuelles: calculsRapport?.puissancesActuelles || {},
                        puissancesProposees: calculsRapport?.puissancesProposees || {},
                        puissancePreconisee: puissancePreconisee,
                        cmdpsOverride: calculsRapport?.cmdpsOverride || null
                      },
                      formuleActuelle: calculsRapport?.formuleActuelle || pdlData?.formule_actuelle || '',
                      formuleProposee: calculsRapport?.formuleProposee || pdlData?.formule_actuelle || '',
                      tensionActuelle: calculsRapport?.tensionActuelle || pdlData?.tension || 'HTA',
                      graphData: graphDataRaw,
                      puissancesFactures: Object.keys(puissancesFacturesParPDL[pdlKey] || {}).length > 0
                        ? Object.entries(puissancesFacturesParPDL[pdlKey]).filter(([,v]) => v > 0).map(([mois, puissance]) => ({ mois, puissance }))
                        : undefined,
                      plagesHorairesCustom: plagesHorairesCustomRef.current[pdlKey] || null
                    };
                    
                    // Envoyer au backend et récupérer un ID de preview
                    try {
                      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/turpe/rapports/preview`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(rapportData)
                      });
                      if (response.ok) {
                        const { preview_id } = await response.json();
                        window.open(`/rapports/turpe/preview?preview=${preview_id}`, '_blank');
                        
                        // Sauvegarder aussi le rapport en base de données
                        const token = localStorage.getItem('token');
                        const monoSaveUrl = editMode 
                          ? `${process.env.REACT_APP_BACKEND_URL}/api/turpe/rapports/${editRapportId}`
                          : `${process.env.REACT_APP_BACKEND_URL}/api/turpe/rapports`;
                        const saveResponse = await fetch(monoSaveUrl, {
                          method: editMode ? 'PUT' : 'POST',
                          headers: { 
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                          },
                          body: JSON.stringify({
                            ...rapportData,
                            siren: formData.siren || '',
                            nom_entreprise: formData.nom_client || '',
                            economies: (calculsRapport?.totalActuel || 0) - (calculsRapport?.totalPropose || 0),
                            commercial_id: formData.commercial_id,
                            agence_id: formData.agence_id
                          })
                        });
                        if (saveResponse.ok) {
                          console.log('Rapport TURPE mono sauvegardé en base');
                        }
                      } else {
                        alert('Erreur lors de la création de la preview');
                      }
                    } catch (error) {
                      console.error('Erreur:', error);
                      alert('Erreur lors de la création de la preview');
                    }
                  }
                }}
                className="flex items-center justify-center gap-2 bg-[#3B82F6] hover:bg-[#2563EB] text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all"
              >
                <Eye className="w-5 h-5" />
                Voir le Rapport Interactif
              </button>
              
              {/* Bouton Télécharger PDF */}
              <button
                onClick={async () => {
                  if (analyseType === 'multi') {
                    // === PDF MULTI-PDL ===
                    const depsCalc = buildDepsCalc();
                    const pdlsData = formData.pdls.map((pdlConfig, idx) => {
                      const pdlKey = pdlConfig.pdl;
                      const resultatPDL = puissancesMaxParPDL[pdlKey];
                      // Recalcul complet du PDL au moment de la génération (indépendant du rendu
                      // paginé de l'étape 6) → évite les PDL à 0 sur les gros dossiers (~200 PDL).
                      const calculsPDL = computeCalculsPDL(pdlKey, resultatPDL, depsCalc) || calculsParPDL[pdlKey] || {};
                      const tousLesPoints = resultatPDL?.tousLesPoints || [];
                      const graphDataRaw = capGraphData(agregerParHeure(tousLesPoints)
                        .map(p => ({ date: p.horodate, kW: p.kW, kVA: p.kVA })));
                      
                      const consoParPlage = calculsPDL.consommations || consommationsParPlageRef.current[pdlKey] || {};
                      const puissancesActuellesParPlage = calculsPDL.puissancesActuelles || pdlConfig.puissances_actuelles || {};
                      const puissMax = calculsPDL.puissanceSouscrite || Math.max(
                        puissancesActuellesParPlage?.Pointe || 0,
                        puissancesActuellesParPlage?.HPH || 0,
                        puissancesActuellesParPlage?.HCH || 0,
                        puissancesActuellesParPlage?.HPB || 0,
                        puissancesActuellesParPlage?.HCB || 0,
                        puissancesActuellesParPlage?.max || 0
                      );
                      const puissPreconisee = calculsPDL.puissancePreconisee || preconisationsPuissanceRef.current[pdlKey] || puissMax;
                      
                      return {
                        pdl: {
                          numero: pdlKey,
                          puissanceSouscrite: puissMax
                        },
                        tensionActuelle: pdlConfig.tension || 'HTA',
                        formuleActuelle: pdlConfig.formule_actuelle || '',
                        formuleProposee: calculsPDL.formuleProposee || preconisationsPuissanceRef.current[`${pdlKey}_formule`] || pdlConfig.formule_actuelle || '',
                        commentaire: commentairesParPDL[pdlKey] || '', // Commentaire de l'energy manager
                        calculs: {
                          totalActuel: calculsPDL.totalActuel || 0,
                          totalPropose: calculsPDL.totalPropose || 0,
                          composantes: calculsPDL.composantes || {},
                          consommations: consoParPlage,
                          puissancesActuelles: puissancesActuellesParPlage,
                          puissancesProposees: calculsPDL.puissancesProposees || { max: puissPreconisee },
                          puissancePreconisee: puissPreconisee,
                          cmdpsOverride: calculsPDL.cmdpsOverride || null
                        },
                        graphData: graphDataRaw,
                        puissancesFactures: (() => {
                          const pf = puissancesFacturesParPDL[pdlKey] || {};
                          const entries = Object.entries(pf).filter(([,v]) => v > 0);
                          return entries.length > 0 ? entries.map(([mois, puissance]) => ({ mois, puissance })) : undefined;
                        })()
                      };
                    });
                    
                    const totalActuel = pdlsData.reduce((sum, p) => sum + (p.calculs?.totalActuel || 0), 0);
                    const totalPropose = pdlsData.reduce((sum, p) => sum + (p.calculs?.totalPropose || 0), 0);
                    
                    const rapportMultiData = {
                      client: { 
                        nom: formData.nom_client || '',
                        siren: formData.siren || ''
                      },
                      siren: formData.siren || '', // SIREN au niveau racine aussi
                      nom_entreprise: formData.nom_client || '',
                      pdls: pdlsData,
                      totaux: { totalActuel, totalPropose, economie: totalActuel - totalPropose },
                      economies: totalActuel - totalPropose,
                      commercial_id: formData.commercial_id,
                      agence_id: formData.agence_id
                    };
                    
                    try {
                      // 1. Générer le PDF en tâche de fond (évite le timeout 504 sur gros dossiers)
                      setPdfGenerating(true);
                      setPdfElapsed(0);
                      const blob = await generatePdfViaJob(rapportMultiData, setPdfElapsed);
                      const url = window.URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `rapport_turpe_multi_${formData.nom_client || 'client'}_${new Date().toISOString().split('T')[0]}.pdf`;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      window.URL.revokeObjectURL(url);
                      
                      // 2. Sauvegarder le rapport en base
                      const token = localStorage.getItem('token');
                      const saveUrl = editMode 
                        ? `${process.env.REACT_APP_BACKEND_URL}/api/turpe/rapports/${editRapportId}`
                        : `${process.env.REACT_APP_BACKEND_URL}/api/turpe/rapports`;
                      const saveResponse = await fetch(saveUrl, {
                        method: editMode ? 'PUT' : 'POST',
                        headers: { 
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify(rapportMultiData)
                      });
                      
                      if (saveResponse.ok) {
                        console.log('Rapport TURPE multi-sites sauvegardé');
                        alert('✅ Rapport multi-sites sauvegardé avec succès !\n\nLe PDF a été téléchargé et le rapport est maintenant visible dans le tableau de bord.');
                      } else {
                        const errorText = await saveResponse.text();
                        console.warn('Erreur sauvegarde rapport:', errorText);
                        alert('⚠️ Le PDF a été téléchargé mais la sauvegarde a échoué.\n\nErreur: ' + errorText);
                      }
                      
                    } catch (error) {
                      console.error('Erreur:', error);
                      alert('Erreur lors du téléchargement du PDF multi-sites : ' + (error?.message || error));
                    } finally {
                      setPdfGenerating(false);
                    }
                  } else {
                    // === PDF MONO-PDL ===
                    const pdlData = formData.pdls[0] || currentPDL;
                    
                    // Trouver l'agence sélectionnée
                    const selectedAgence = agences.find(a => a.id === formData.agence_id);
                    const selectedCommercial = commerciaux.find(c => c.id === formData.commercial_id);
                    
                    // Récupérer les données graphiques pour le PDF
                    const pdlKey = calculsRapport?.pdl || pdlData?.pdl;
                    const resultatPDL = puissancesMaxParPDL[pdlKey];
                    const tousLesPoints = resultatPDL?.tousLesPoints || [];
                    const graphDataForPdf = agregerParHeure(tousLesPoints)
                      .map(p => ({ date: p.horodate, kW: p.kW, kVA: p.kVA }));
                    
                    // Puissances pour la courbe - prendre les puissances actuelles par plage
                    const puissancesActuellesParPlage = pdlData?.puissances_actuelles || calculsRapport?.puissancesActuelles || {};
                    const pSouscrite = Math.max(
                      puissancesActuellesParPlage?.Unique || 0,
                      puissancesActuellesParPlage?.HP || 0,
                      puissancesActuellesParPlage?.HC || 0,
                      puissancesActuellesParPlage?.Pointe || 0,
                      puissancesActuellesParPlage?.HPH || 0,
                      puissancesActuellesParPlage?.HCH || 0,
                      puissancesActuellesParPlage?.HPB || 0,
                      puissancesActuellesParPlage?.HCB || 0,
                      puissancesActuellesParPlage?.max || 0,
                      calculsRapport?.puissanceSouscrite || 0
                    );
                    const pPreconisee = calculsRapport?.puissancePreconisee || calculsRapport?.puissanceProposee || pSouscrite;
                    
                    const rapportData = {
                      client: {
                        nom: formData.nom_client || '',
                        siren: formData.siren || '',
                        site: ''
                      },
                      siren: formData.siren || '', // SIREN au niveau racine
                      nom_entreprise: formData.nom_client || '',
                      pdl: {
                        numero: calculsRapport?.pdl || pdlData?.pdl || '',
                        puissanceSouscrite: pSouscrite
                      },
                      commentaire: commentairesParPDL[pdlKey] || '', // Commentaire de l'energy manager
                      calculs: {
                        totalActuel: calculsRapport?.totalActuel || 0,
                        totalPropose: calculsRapport?.totalPropose || 0,
                        composantes: calculsRapport?.composantes || {},
                        consommations: calculsRapport?.consommations || {},
                        nbJours: calculsRapport?.nbJoursData || 0,
                        puissancesActuelles: calculsRapport?.puissancesActuelles || {},
                        puissancesProposees: calculsRapport?.puissancesProposees || {},
                        puissancePreconisee: pPreconisee,
                        cmdpsOverride: calculsRapport?.cmdpsOverride || null
                      },
                      formuleActuelle: calculsRapport?.formuleActuelle || pdlData?.formule_actuelle || '',
                      formuleProposee: calculsRapport?.formuleProposee || pdlData?.formule_actuelle || '',
                      tensionActuelle: calculsRapport?.tensionActuelle || pdlData?.tension || 'HTA',
                      graphData: graphDataForPdf,
                      puissancesFactures: (() => {
                        const pdlKeyPdf = calculsRapport?.pdl || pdlData?.pdl;
                        const pf = puissancesFacturesParPDL[pdlKeyPdf] || {};
                        const entries = Object.entries(pf).filter(([,v]) => v > 0);
                        return entries.length > 0 ? entries.map(([mois, puissance]) => ({ mois, puissance })) : undefined;
                      })(),
                      agence: selectedAgence ? {
                        nom: selectedAgence.nom,
                        commercial: selectedCommercial ? `${selectedCommercial.prenom} ${selectedCommercial.nom}` : '',
                        email: selectedCommercial?.email || 'contact@placedesenergies.com',
                        telephone: selectedCommercial?.telephone || ''
                      } : null,
                      plagesHorairesCustom: (() => {
                        const pdlKeyCustom = calculsRapport?.pdl || pdlData?.pdl;
                        return plagesHorairesCustomRef.current[pdlKeyCustom] || null;
                      })()
                    };
                    
                    try {
                      // 1. Générer et télécharger le PDF
                      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/turpe/rapports/pdf`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(rapportData)
                      });
                      
                      if (!response.ok) throw new Error('Erreur génération PDF');
                      
                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const link = document.createElement('a');
                      link.href = url;
                      link.download = `rapport_turpe_${rapportData.client.nom || 'client'}_${new Date().toISOString().split('T')[0]}.pdf`;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      window.URL.revokeObjectURL(url);
                      
                      // 2. Sauvegarder le rapport en base
                      const token = localStorage.getItem('token');
                      const rapportToSave = {
                        ...rapportData,
                        economies: (calculsRapport?.totalActuel || 0) - (calculsRapport?.totalPropose || 0),
                        commercial_id: formData.commercial_id,
                        agence_id: formData.agence_id
                      };
                      
                      const saveUrl = editMode 
                        ? `${process.env.REACT_APP_BACKEND_URL}/api/turpe/rapports/${editRapportId}`
                        : `${process.env.REACT_APP_BACKEND_URL}/api/turpe/rapports`;
                      const saveResponse = await fetch(saveUrl, {
                        method: editMode ? 'PUT' : 'POST',
                        headers: { 
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${token}`
                        },
                        body: JSON.stringify(rapportToSave)
                      });
                      
                      if (saveResponse.ok) {
                        console.log('Rapport TURPE mono-PDL sauvegardé');
                        alert('✅ Rapport sauvegardé avec succès !\n\nLe PDF a été téléchargé et le rapport est maintenant visible dans le tableau de bord.');
                      } else {
                        const errorText = await saveResponse.text();
                        console.warn('Erreur sauvegarde rapport:', errorText);
                        alert('⚠️ Le PDF a été téléchargé mais la sauvegarde a échoué.\n\nErreur: ' + errorText);
                      }
                      
                    } catch (error) {
                      console.error('Erreur:', error);
                      alert('Erreur lors du téléchargement du PDF');
                    }
                  }
                }}
                disabled={pdfGenerating}
                className="flex items-center justify-center gap-2 border-2 border-[#397DFF] text-[#397DFF] hover:bg-[#397DFF] hover:text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="download-pdf-btn"
              >
                <Download className="w-5 h-5" />
                {pdfGenerating ? 'Génération en cours…' : 'Télécharger en PDF'}
              </button>
              <MessageFournisseurButton
                pdls={getMessagePdls}
                client={formData?.nom_entreprise || formData?.client?.nom}
                siren={formData?.siren}
                triggerClassName="flex items-center justify-center gap-2 border-2 border-[#312e81] text-[#312e81] hover:bg-[#312e81] hover:text-white px-8 py-4 rounded-lg text-lg font-semibold transition-all"
                triggerLabel="Message fournisseur"
              />
            </div>

            {/* Bandeau de progression génération PDF (gros dossiers) */}
            {pdfGenerating && (
              <div className="mt-4 p-4 bg-[#EFF6FF] border border-[#397DFF]/40 rounded-lg flex items-center gap-3" data-testid="pdf-generating-banner">
                <div className="animate-spin w-5 h-5 border-2 border-[#397DFF] border-t-transparent rounded-full"></div>
                <div className="text-sm text-[#1E3A8A]">
                  Génération du PDF en cours… {pdfElapsed > 0 ? `(${pdfElapsed}s)` : ''}
                  <span className="block text-xs text-[#687175]">Les gros dossiers (nombreux PDL) peuvent nécessiter jusqu'à une minute. Merci de patienter, le téléchargement démarrera automatiquement.</span>
                </div>
              </div>
            )}
            
            {/* Aperçu des calculs */}
            {calculsRapport && analyseType !== 'multi' && (
              <div className="mt-6 p-4 bg-[#F0FDF9] border border-[#0F9D7E]/30 rounded-lg">
                <h4 className="font-semibold text-[#0F9D7E] mb-2">Résumé des calculs</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-[#687175]">Coût actuel:</span>
                    <span className="block font-mono font-semibold">{calculsRapport.totalActuel?.toLocaleString('fr-FR', {minimumFractionDigits: 2})} €</span>
                  </div>
                  <div>
                    <span className="text-[#687175]">Coût optimisé:</span>
                    <span className="block font-mono font-semibold">{calculsRapport.totalPropose?.toLocaleString('fr-FR', {minimumFractionDigits: 2})} €</span>
                  </div>
                  <div>
                    <span className="text-[#687175]">Économie:</span>
                    <span className={`block font-mono font-semibold ${calculsRapport.economie >= 0 ? 'text-[#0F9D7E]' : 'text-[#EF4444]'}`}>
                      {calculsRapport.economie >= 0 ? '+' : ''}{calculsRapport.economie?.toLocaleString('fr-FR', {minimumFractionDigits: 2})} €
                    </span>
                  </div>
                  <div>
                    <span className="text-[#687175]">Formule proposée:</span>
                    <span className="block font-semibold">{calculsRapport.formuleProposee}</span>
                  </div>
                </div>
              </div>
            )}
            
            {/* Aperçu multi-PDL */}
            {analyseType === 'multi' && formData.pdls.length > 0 && (
              <div className="mt-6 p-4 bg-[#F0FDF9] border border-[#0F9D7E]/30 rounded-lg text-left">
                <h4 className="font-semibold text-[#0F9D7E] mb-3">Synthèse Multi-Sites</h4>
                <div className="space-y-2">
                  {formData.pdls.map((pdlConfig, idx) => {
                    const resultatPDL = puissancesMaxParPDL[pdlConfig.pdl];
                    return (
                      <div key={idx} className="flex justify-between text-sm py-1 border-b border-[#0F9D7E]/20 last:border-0">
                        <span className="font-mono">{pdlConfig.pdl || `PDL ${idx + 1}`}</span>
                        <span className="text-[#687175]">{pdlConfig.tension} - {pdlConfig.formule_actuelle}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-between gap-3 pt-6">
            <button onClick={() => setStep(6)} className="px-6 py-3 border border-[#BFC6CC] text-[#687175] rounded-lg">
              ← Retour à la Validation
            </button>
            <button
              onClick={sauvegarderBrouillon}
              className="flex items-center gap-2 px-4 py-2 border border-[#F9C80E] text-[#F9C80E] hover:bg-[#F9C80E] hover:text-white rounded-lg"
            >
              💾 Enregistrer Brouillon
            </button>
          </div>
        </div>
  );
};

export default Step7Generation;
